import type { MetagameTokenPackageBundle } from '../../dto/metagames.types';
import type { SupportedLanguages } from '../../i18n/constants';
import type { BenefitPackage, EventTriggerPopupData } from '../event-trigger/types';
import type { SubscriptionPlanEnhanced, SubscriptionType } from '../subscription/types';

export type OfferType = 'regular' | 'welcome' | 'low_balance';

export interface EventTriggerPurchaseData {
  activeEvent: EventTriggerPopupData;
}

type RegularBundleType = 'coinBack' | 'freeSpins' | 'metagameToken' | 'metagameFeature' | 'challenge';

type RegularBundlePayload = {
  gcCoin?: number;
  scCoin?: number;
};

export type PackageBundle = {
  id: string;
  packageId: number;
  bundleType: RegularBundleType;
  bundlePayload: RegularBundlePayload | MetagameTokenPackageBundle['bundlePayload'];
};

export type MetagamesTokenPackageBundles = PackageBundle & {
  bundleType: 'metagameToken';
  bundlePayload: MetagameTokenPackageBundle['bundlePayload'];
};

export enum STORE_PACKAGE_LABEL {
  DEFAULT = 'Default',
  TIER_EXCLUSIVE = 'Tier Exclusive',
  DYNASTY = 'Dynasty',
  ROYAL_CROWNS = 'Royal Crowns',
}

export enum STORE_PACKAGE_LABEL_COLOR {
  PINK = 'Pink',
  BLUE = 'Blue',
  PURPLE = 'Purple',
}

export type StorePackageLabelDataType = Partial<
  Record<
    SupportedLanguages,
    {
      labelText: string;
      labelColor: STORE_PACKAGE_LABEL_COLOR;
    }
  >
>;

export type StorePackageLabelType = string | STORE_PACKAGE_LABEL | null;

export interface IPackage {
  imageUrl: string;
  gcAmount: number;
  scAmount: number;
  price: number;
  packageId: number;
  eventTriggerData?: EventTriggerPurchaseData;
  packageBundles?: PackageBundle[];
  subscriptionData?: SubscriptionPlanEnhanced;
  labelData?: StorePackageLabelDataType;
}

export type IOrderPackage = Omit<IPackage, 'imageUrl'>;

export interface ISubscriptionOrderPackage {
  package: BenefitPackage;
  price: number;
  subscriptionType: SubscriptionType;
  eventTriggerData?: EventTriggerPurchaseData;
  packageBundles?: MetagameTokenPackageBundle[];
}

export interface IStorePackage extends IPackage {
  order: number;
  type?: StorePackageLabelType;
}

export interface IStoreParameters {
  isNpuStore: boolean;
}

export interface IStore {
  key: string;
  title: string;
  storeParameters: IStoreParameters;
  storePackages: IStorePackage[];
}

export interface IOffer extends IPackage {
  key: string;
  title: string;
  name: string;
  type: OfferType;
  startDate: string;
  endDate: string;
}

export enum EnumTransactionStatuses {
  PENDING = 0,
  SUCCESS = 1,
  REJECTED = 2,
  CANCELED = 3,
  ROLLBACK = 4,
  APPROVED = 5,
  FAILED = 6,
  INPROGRESS = 7,
  POSTPONE = 8,
  REFUND = 16,
  PENDING_REFUND = 17,
  ACTIVATION = 18,
}

export interface MetagameFeatureTransactionPayload {
  amount: string | number;
  featureType: string;
  metagameFeatureId: number;
  coinType: string;
  symbolUrl: string;
  metagameTypeName: string;
}

export interface MetagameFeaturePackageBundle {
  id?: string;
  packageId?: number;
  bundleType: 'metagameFeature';
  bundlePayload: MetagameFeatureTransactionPayload;
}

export type TransactionPackageBundleItem = MetagameTokenPackageBundle | MetagameFeaturePackageBundle;

export interface IPackageData {
  name?: string;
  amount?: number;
  gcCoin?: number;
  scCoin?: number;
  isActive?: boolean;
  packageId?: number;
  bundleItems?: TransactionPackageBundleItem[];
}

export interface ITransactionsData {
  created_at: string;
  gccoin: number;
  id: number;
  sccoin: number;
  status: EnumTransactionStatuses;
  transactiontype: string;
  amount: number;
  moreDetails?: {
    traceId?: string;
    gcAmount?: number;
    itemName?: string;
    scAmount?: number;
    packageId?: number;
    purchaseSource?: string;
    packageData?: IPackageData;
  };
}

export enum SystemErrorCodes {
  CVV_VALUE_NOT_PRESENT = 110,
  E_YOU_SUBMITTED_AN_INCORRECT_CVV_VALUE_WITH_YOUR_REQUEST = 53,
  E_YOUR_REQUEST_HAS_FAILED_THE_CVV_CHECK = 66,
  E_YOUR_REQUEST_HAS_BEEN_DECLINED_BY_THE_ISSUING_BANK_DUE_TO_ITS_PROPRIETARY_CARD_ACTIVITY_REGULATIONS = 69,
  TRANSACTION_DECLINED = 97,
  REQUEST_DECLINED = 57,
  E_YOU_SUBMITTED_A_CARD_TYPE_FOR_WHICH_THE_MERCHANT_ACCOUNT_IS_NOT_CONFIGURED = 56,
  E_INTERNAL_SERVER_ERROR = 17,
  E_THE_ZIP_POSTAL_CODE_MUST_BE_PROVIDED_FOR_AN_AVS_CHECK_REQUEST = 52,
  E_YOUR_REQUEST_HAS_FAILED_THE_AVS_CHECK_NOTE_THAT_THE_AMOUNT_HAS_STILL_BEEN_RESERVED_ON_THE_CUSTOMER = 55,
  E_EXPIRED_CARD = 105,
  E_EXPIRED_DATE_CARD = 54,
  E_DECLINE = 81,
}

export enum ProviderErrorCodes {
  TRANSACTION_DECLINED = 3054,
  REQUEST_DECLINED = 3009,
}
